import { FloorNames } from "@common/utils/terrain";
import { Vec, type Vector } from "@common/utils/vector";
import { CanvasSource, Container, Rectangle, Sprite, Texture } from "pixi.js";
import { CameraManager } from "../managers/cameraManager";
import { MapManager } from "../managers/mapManager";
import { PIXI_SCALE } from "../utils/constants";
import { mudZone } from "./warNoise";

type Rand = () => number;
const mulberry32 = (seed: number): Rand => (): number => {
    let t = (seed = (seed + 0x6D2B79F5) | 0);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const rgba = (hex: string, a: number): string => {
    const v = parseInt(hex.slice(1), 16);
    return `rgba(${v >> 16},${(v >> 8) & 255},${v & 255},${a})`;
};

/** an irregular closed outline: radius r modulated by a few random harmonics */
const blob = (ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, wobble: number, rand: Rand, points = 64): void => {
    const phases = [rand() * 6.28, rand() * 6.28, rand() * 6.28, rand() * 6.28];
    const amps = [wobble * 0.5, wobble * 0.3, wobble * 0.15, wobble * 0.08];
    ctx.beginPath();
    for (let i = 0; i <= points; i++) {
        const a = (i / points) * Math.PI * 2;
        const k = 1 + amps[0] * Math.sin(a * 2 + phases[0]) + amps[1] * Math.sin(a * 3 + phases[1]) + amps[2] * Math.sin(a * 5 + phases[2]) + amps[3] * Math.sin(a * 9 + phases[3]);
        const x = cx + Math.cos(a) * r * k, y = cy + Math.sin(a) * r * k;
        i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
    }
    ctx.closePath();
};

const speckle = (ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, count: number, rand: Rand, dark: string, light: string): void => {
    for (let i = 0; i < count; i++) {
        const a = rand() * 6.28, d = Math.sqrt(rand()) * r;
        ctx.fillStyle = rand() < 0.55 ? rgba(dark, 0.12 + rand() * 0.18) : rgba(light, 0.08 + rand() * 0.14);
        const s = 0.8 + rand() * 2.2;
        ctx.fillRect(cx + Math.cos(a) * d, cy + Math.sin(a) * d, s, s);
    }
};

// light comes from the top-left, like the relief in the ground shader
const LIGHT = -Math.PI * 0.75;

function paintCrater(ctx: CanvasRenderingContext2D, ox: number, oy: number, rand: Rand, flooded: boolean): void {
    const cx = ox + 128, cy = oy + 128;
    // contact shadow: the crater sits in the ground
    const shade = ctx.createRadialGradient(cx, cy, 40, cx, cy, 124);
    shade.addColorStop(0, "rgba(10,8,5,0.42)"); shade.addColorStop(0.55, "rgba(10,8,5,0.22)"); shade.addColorStop(1, "rgba(10,8,5,0)");
    ctx.fillStyle = shade; ctx.fillRect(ox, oy, 256, 256);
    // ejecta: thrown earth streaks and clods beyond the rim
    for (let i = 0; i < 54; i++) {
        const a = rand() * 6.28, r0 = 62 + rand() * 10, len = 20 + rand() * 42;
        ctx.strokeStyle = rand() < 0.5 ? rgba("#241d13", 0.22 + rand() * 0.25) : rgba("#5e503a", 0.18 + rand() * 0.22);
        ctx.lineWidth = 1.2 + rand() * 3.8; ctx.lineCap = "round";
        ctx.beginPath(); ctx.moveTo(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0);
        ctx.lineTo(cx + Math.cos(a + (rand() - 0.5) * 0.12) * (r0 + len), cy + Math.sin(a + (rand() - 0.5) * 0.12) * (r0 + len)); ctx.stroke();
    }
    for (let i = 0; i < 70; i++) {
        const a = rand() * 6.28, d = 66 + rand() * 50;
        ctx.fillStyle = rand() < 0.5 ? rgba("#2b2317", 0.5 + rand() * 0.4) : rgba("#6b5b3e", 0.35 + rand() * 0.4);
        ctx.beginPath(); ctx.ellipse(cx + Math.cos(a) * d, cy + Math.sin(a) * d, 1.2 + rand() * 3.4, 1 + rand() * 2.4, rand() * 3, 0, 6.28); ctx.fill();
    }
    // raised rim, lit from the top-left
    blob(ctx, cx, cy, 70, 0.1, rand);
    const rim = ctx.createRadialGradient(cx, cy, 44, cx, cy, 78);
    rim.addColorStop(0, "#4a3f2b"); rim.addColorStop(0.7, "#5f513a"); rim.addColorStop(1, "rgba(74,63,43,0)");
    ctx.fillStyle = rim; ctx.fill();
    ctx.save(); ctx.clip();
    const lit = ctx.createLinearGradient(cx + Math.cos(LIGHT) * 70, cy + Math.sin(LIGHT) * 70, cx - Math.cos(LIGHT) * 70, cy - Math.sin(LIGHT) * 70);
    lit.addColorStop(0, "rgba(150,132,96,0.42)"); lit.addColorStop(0.5, "rgba(0,0,0,0)"); lit.addColorStop(1, "rgba(12,9,5,0.4)");
    ctx.fillStyle = lit; ctx.fillRect(ox, oy, 256, 256);
    ctx.restore();
    // the bowl: darkest in the middle, its top-left inner wall in shadow
    blob(ctx, cx, cy, 52, 0.12, rand);
    const bowl = ctx.createRadialGradient(cx + 8, cy + 8, 4, cx, cy, 58);
    bowl.addColorStop(0, "#130f09"); bowl.addColorStop(0.65, "#1f1910"); bowl.addColorStop(1, "#2e2619");
    ctx.fillStyle = bowl; ctx.fill();
    ctx.save(); ctx.clip();
    const wall = ctx.createLinearGradient(cx + Math.cos(LIGHT) * 52, cy + Math.sin(LIGHT) * 52, cx - Math.cos(LIGHT) * 52, cy - Math.sin(LIGHT) * 52);
    wall.addColorStop(0, "rgba(0,0,0,0.45)"); wall.addColorStop(0.55, "rgba(0,0,0,0)"); wall.addColorStop(1, "rgba(120,104,76,0.22)");
    ctx.fillStyle = wall; ctx.fillRect(ox, oy, 256, 256);
    speckle(ctx, cx, cy, 52, 260, rand, "#050403", "#6b5b3e");
    ctx.restore();
    if (flooded) {
        // murky water pooled in the bottom, with a scum line and a cold glint
        blob(ctx, cx + 3, cy + 3, 40, 0.12, rand);
        const water = ctx.createRadialGradient(cx - 10, cy - 10, 4, cx, cy, 44);
        water.addColorStop(0, "#3b3f31"); water.addColorStop(0.7, "#282b21"); water.addColorStop(1, "#1d1f18");
        ctx.fillStyle = water; ctx.fill();
        ctx.strokeStyle = "rgba(92,82,56,0.7)"; ctx.lineWidth = 2.6; ctx.stroke();
        ctx.save(); ctx.clip();
        ctx.strokeStyle = "rgba(168,170,150,0.42)"; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.arc(cx + 3, cy + 3, 30, LIGHT - 0.55, LIGHT + 0.45); ctx.stroke();
        ctx.strokeStyle = "rgba(170,182,160,0.18)"; ctx.lineWidth = 1.4;
        ctx.beginPath(); ctx.arc(cx + 3, cy + 3, 22, LIGHT - 0.3, LIGHT + 0.25); ctx.stroke();
        ctx.restore();
    }
}

function paintSmallHole(ctx: CanvasRenderingContext2D, ox: number, oy: number, rand: Rand): void {
    const cx = ox + 64, cy = oy + 64;
    const shade = ctx.createRadialGradient(cx, cy, 16, cx, cy, 60);
    shade.addColorStop(0, "rgba(10,8,5,0.4)"); shade.addColorStop(1, "rgba(10,8,5,0)");
    ctx.fillStyle = shade; ctx.fillRect(ox, oy, 128, 128);
    for (let i = 0; i < 26; i++) {
        const a = rand() * 6.28, d = 30 + rand() * 26;
        ctx.fillStyle = rand() < 0.5 ? rgba("#2b2317", 0.6) : rgba("#6b5b3e", 0.45);
        ctx.beginPath(); ctx.ellipse(cx + Math.cos(a) * d, cy + Math.sin(a) * d, 1 + rand() * 2.4, 0.8 + rand() * 1.8, rand() * 3, 0, 6.28); ctx.fill();
    }
    blob(ctx, cx, cy, 33, 0.14, rand, 40);
    ctx.fillStyle = "#56493a"; ctx.fill();
    blob(ctx, cx, cy, 24, 0.16, rand, 40);
    const bowl = ctx.createRadialGradient(cx + 4, cy + 4, 2, cx, cy, 26);
    bowl.addColorStop(0, "#110d08"); bowl.addColorStop(1, "#2a2217");
    ctx.fillStyle = bowl; ctx.fill();
}

function paintScorch(ctx: CanvasRenderingContext2D, ox: number, oy: number, rand: Rand): void {
    const cx = ox + 128, cy = oy + 128;
    for (let i = 0; i < 26; i++) {
        const a = rand() * 6.28, d = rand() * 46;
        const r = 30 + rand() * 46;
        const g = ctx.createRadialGradient(cx + Math.cos(a) * d, cy + Math.sin(a) * d, 0, cx + Math.cos(a) * d, cy + Math.sin(a) * d, r);
        g.addColorStop(0, "rgba(14,11,8,0.34)"); g.addColorStop(1, "rgba(14,11,8,0)");
        ctx.fillStyle = g; ctx.fillRect(ox, oy, 256, 256);
    }
    for (let i = 0; i < 40; i++) {
        const a = rand() * 6.28, r0 = 20 + rand() * 30, len = 30 + rand() * 60;
        ctx.strokeStyle = rgba("#0d0a07", 0.12 + rand() * 0.18); ctx.lineWidth = 2 + rand() * 6; ctx.lineCap = "round";
        ctx.beginPath(); ctx.moveTo(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0); ctx.lineTo(cx + Math.cos(a) * (r0 + len), cy + Math.sin(a) * (r0 + len)); ctx.stroke();
    }
    speckle(ctx, cx, cy, 70, 160, rand, "#050403", "#6d665a");
    for (let i = 0; i < 9; i++) {
        const a = rand() * 6.28, d = rand() * 40;
        ctx.fillStyle = rgba("#c2702e", 0.45 + rand() * 0.35);
        ctx.fillRect(cx + Math.cos(a) * d, cy + Math.sin(a) * d, 1.5, 1.5);
    }
}

function paintPuddle(ctx: CanvasRenderingContext2D, ox: number, oy: number, rand: Rand): void {
    const cx = ox + 128, cy = oy + 128;
    blob(ctx, cx, cy, 86, 0.34, rand, 80);
    ctx.fillStyle = "rgba(60,54,38,0.55)"; ctx.fill();
    blob(ctx, cx, cy, 76, 0.36, rand, 80);
    const g = ctx.createRadialGradient(cx - 22, cy - 22, 6, cx, cy, 92);
    g.addColorStop(0, "rgba(64,66,54,0.92)"); g.addColorStop(0.6, "rgba(34,36,28,0.92)"); g.addColorStop(1, "rgba(28,29,22,0.9)");
    ctx.fillStyle = g; ctx.fill();
    ctx.save(); ctx.clip();
    for (let i = 0; i < 6; i++) {
        ctx.strokeStyle = rgba("#b3bda8", 0.12 + rand() * 0.2); ctx.lineWidth = 1 + rand() * 2;
        const a = LIGHT + (rand() - 0.5) * 0.9, d = 20 + rand() * 40;
        ctx.beginPath(); ctx.ellipse(cx + Math.cos(a) * d, cy + Math.sin(a) * d, 6 + rand() * 16, 1.5 + rand() * 3, a + 1.57, 0, 6.28); ctx.stroke();
    }
    ctx.restore();
}

function paintMudPatch(ctx: CanvasRenderingContext2D, ox: number, oy: number, rand: Rand): void {
    const cx = ox + 128, cy = oy + 128;
    for (let i = 0; i < 12; i++) {
        blob(ctx, cx + (rand() - 0.5) * 80, cy + (rand() - 0.5) * 80, 20 + rand() * 44, 0.4, rand, 36);
        ctx.fillStyle = rand() < 0.65 ? rgba("#1d1810", 0.22 + rand() * 0.2) : rgba("#5e503a", 0.14 + rand() * 0.14);
        ctx.fill();
    }
    speckle(ctx, cx, cy, 100, 120, rand, "#0e0b07", "#6b5b3e");
}

function paintRuts(ctx: CanvasRenderingContext2D, ox: number, oy: number, rand: Rand): void {
    // two wheel ruts along the length (x), 256x128
    for (const lane of [44, 84]) {
        for (let x = 0; x < 256; x += 3) {
            const y = oy + lane + Math.sin((x + rand() * 4) * 0.05) * 3;
            const fade = Math.min(1, x / 30, (256 - x) / 30);
            ctx.fillStyle = rgba("#16110b", 0.32 * fade);
            ctx.fillRect(ox + x, y - 5 - rand() * 2, 3, 10 + rand() * 4);
            ctx.fillStyle = rgba("#6b5b3e", 0.16 * fade);
            ctx.fillRect(ox + x, y - 9 - rand() * 2, 3, 2);
        }
        for (let i = 0; i < 4; i++) {
            const x = 30 + rand() * 196;
            blob(ctx, ox + x, oy + lane, 6 + rand() * 7, 0.3, rand, 20);
            ctx.fillStyle = "rgba(38,44,36,0.8)"; ctx.fill();
        }
    }
}

function paintDebris(ctx: CanvasRenderingContext2D, ox: number, oy: number, kind: number, rand: Rand): void {
    const cx = ox + 32, cy = oy + 32;
    ctx.save(); ctx.translate(cx, cy); ctx.rotate(rand() * 6.28);
    switch (kind) {
        case 0: case 1: // spent cartridge cases
            for (let i = 0; i < (kind ? 3 : 1); i++) {
                ctx.fillStyle = "#9a7d3a"; ctx.fillRect(-2 + i * 7, -7 + i * 3, 4, 13);
                ctx.fillStyle = "#5d4a22"; ctx.fillRect(-2 + i * 7, 5 + i * 3, 4, 2);
            }
            break;
        case 2: case 3: // splinters of wood
            for (let i = 0; i < 3; i++) {
                ctx.fillStyle = i % 2 ? "#6a5a3e" : "#4f4230";
                ctx.fillRect(-12 + i * 4, -3 + i * 5, 24 - i * 6, 3);
            }
            break;
        case 4: // stones
            for (let i = 0; i < 4; i++) {
                ctx.fillStyle = rgba(i % 2 ? "#6f6a5e" : "#56524a", 0.95);
                ctx.beginPath(); ctx.ellipse((rand() - 0.5) * 20, (rand() - 0.5) * 20, 3 + rand() * 4, 2 + rand() * 3, rand() * 3, 0, 6.28); ctx.fill();
            }
            break;
        case 5: // torn sandbag
            blob(ctx, 0, 0, 13, 0.3, rand, 24); ctx.fillStyle = "#7d7050"; ctx.fill();
            ctx.strokeStyle = "rgba(40,34,22,0.6)"; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(-8, 0); ctx.lineTo(8, 1); ctx.stroke();
            break;
        case 6: // rusted tin
            ctx.fillStyle = "#5b3a20"; ctx.beginPath(); ctx.ellipse(0, 0, 6, 9, 0, 0, 6.28); ctx.fill();
            ctx.fillStyle = "#7a5230"; ctx.beginPath(); ctx.ellipse(-1, -2, 4, 6, 0, 0, 6.28); ctx.fill();
            break;
        default: // scrap of paper / map
            ctx.fillStyle = "#a39c85"; ctx.fillRect(-8, -6, 16, 12);
            ctx.fillStyle = "rgba(60,54,40,0.4)"; ctx.fillRect(-6, -3, 12, 1); ctx.fillRect(-6, 1, 9, 1);
    }
    ctx.restore();
}

function paintWire(ctx: CanvasRenderingContext2D, ox: number, oy: number, rand: Rand): void {
    // a coil of barbed wire between two pickets, 256 wide
    const y = oy + 128;
    for (const px of [22, 234]) {
        ctx.fillStyle = "#3a2f22"; ctx.fillRect(ox + px - 4, y - 6, 8, 12);
        ctx.fillStyle = "#5a4c36"; ctx.fillRect(ox + px - 3, y - 5, 6, 3);
    }
    for (let i = 0; i < 26; i++) {
        const x = ox + 24 + i * 8.2;
        const r = 16 + rand() * 6;
        ctx.strokeStyle = "rgba(34,31,26,0.95)"; ctx.lineWidth = 1.7;
        ctx.beginPath(); ctx.ellipse(x, y + (rand() - 0.5) * 3, r * 0.55, r, 0.35 + (rand() - 0.5) * 0.2, 0, 6.28); ctx.stroke();
        ctx.strokeStyle = "rgba(120,114,100,0.35)"; ctx.lineWidth = 0.7;
        ctx.beginPath(); ctx.ellipse(x - 0.8, y - 0.8, r * 0.55, r, 0.35, 3.6, 5.2); ctx.stroke();
        ctx.strokeStyle = "rgba(34,31,26,0.9)"; ctx.lineWidth = 1;
        const bx = x + (rand() - 0.5) * 8, by = y + (rand() - 0.5) * 2 * r;
        ctx.beginPath(); ctx.moveTo(bx - 2, by - 2); ctx.lineTo(bx + 2, by + 2); ctx.moveTo(bx + 2, by - 2); ctx.lineTo(bx - 2, by + 2); ctx.stroke();
    }
}

function paintDuckboard(ctx: CanvasRenderingContext2D, ox: number, oy: number, rand: Rand, broken: boolean): void {
    // 256 x 96: two rails along the length, slats across
    ctx.fillStyle = "rgba(12,10,7,0.35)"; ctx.fillRect(ox + 2, oy + 14, 252, 72);
    ctx.fillStyle = "#33291c"; ctx.fillRect(ox + 4, oy + 22, 248, 9); ctx.fillRect(ox + 4, oy + 66, 248, 9);
    for (let x = 6; x < 250; x += 17) {
        if (broken && rand() < 0.18) continue;
        const shade = ["#7a6546", "#6a5a3e", "#5a4c36", "#705d40"][Math.floor(rand() * 4)];
        const tilt = broken ? (rand() - 0.5) * 0.12 : (rand() - 0.5) * 0.03;
        ctx.save(); ctx.translate(ox + x + 6, oy + 48); ctx.rotate(tilt);
        ctx.fillStyle = shade; ctx.fillRect(-6, -40 + rand() * 3, 12, 80 - rand() * 4);
        ctx.fillStyle = "rgba(20,16,10,0.25)"; for (let g = 0; g < 3; g++) ctx.fillRect(-5 + g * 4, -38, 1, 76);
        ctx.fillStyle = "rgba(160,140,100,0.18)"; ctx.fillRect(-6, -40, 12, 2);
        ctx.fillStyle = "#2a241b"; ctx.fillRect(-1, -25, 2, 2); ctx.fillRect(-1, 21, 2, 2);
        if (rand() < 0.35) { ctx.fillStyle = rgba("#1d1810", 0.35 + rand() * 0.3); ctx.fillRect(-6, -10 + rand() * 30, 12, 6 + rand() * 12); }
        ctx.restore();
    }
}

function paintFootprint(ctx: CanvasRenderingContext2D, ox: number, oy: number): void {
    // boot print pointing up, 32x64
    ctx.fillStyle = "#1a150e";
    ctx.beginPath(); ctx.ellipse(ox + 16, oy + 20, 9, 14, 0, 0, 6.28); ctx.fill();
    ctx.beginPath(); ctx.ellipse(ox + 16, oy + 49, 7, 9, 0, 0, 6.28); ctx.fill();
    ctx.fillStyle = "rgba(90,78,56,0.5)";
    for (let i = 0; i < 5; i++) ctx.fillRect(ox + 9, oy + 10 + i * 5, 14, 1.4);
}

interface Atlas {
    readonly texture: Texture
    readonly frame: (name: string) => Texture
}

let atlas: Atlas | undefined;
const frames = new Map<string, Rectangle>();

function buildAtlas(): Atlas {
    const canvas = document.createElement("canvas");
    canvas.width = 2048; canvas.height = 1024;
    const ctx = canvas.getContext("2d")!;
    const rand = mulberry32(4242);
    const cell = (name: string, x: number, y: number, w: number, h: number): void => { frames.set(name, new Rectangle(x, y, w, h)); };
    for (let i = 0; i < 3; i++) { paintCrater(ctx, i * 256, 0, rand, false); cell(`crater${i}`, i * 256, 0, 256, 256); }
    for (let i = 0; i < 3; i++) { paintCrater(ctx, (3 + i) * 256, 0, rand, true); cell(`flooded${i}`, (3 + i) * 256, 0, 256, 256); }
    for (let i = 0; i < 2; i++) { paintScorch(ctx, (6 + i) * 256, 0, rand); cell(`scorch${i}`, (6 + i) * 256, 0, 256, 256); }
    for (let i = 0; i < 3; i++) { paintPuddle(ctx, i * 256, 256, rand); cell(`puddle${i}`, i * 256, 256, 256, 256); }
    for (let i = 0; i < 2; i++) { paintWire(ctx, (3 + i) * 256, 256, rand); cell(`wire${i}`, (3 + i) * 256, 256, 256, 256); }
    paintScorch(ctx, 5 * 256, 256, rand); cell("scorch2", 5 * 256, 256, 256, 256);
    for (let i = 0; i < 2; i++) { paintMudPatch(ctx, (6 + i) * 256, 256, rand); cell(`mud${i}`, (6 + i) * 256, 256, 256, 256); }
    for (let i = 0; i < 4; i++) { paintSmallHole(ctx, i * 128, 512, rand); cell(`hole${i}`, i * 128, 512, 128, 128); }
    for (let i = 0; i < 8; i++) { paintDebris(ctx, 512 + i * 64, 512, i, rand); cell(`debris${i}`, 512 + i * 64, 512, 64, 64); }
    for (let i = 0; i < 2; i++) { paintRuts(ctx, 1024 + i * 256, 512, rand); cell(`ruts${i}`, 1024 + i * 256, 512, 256, 128); }
    for (let i = 0; i < 2; i++) { paintDuckboard(ctx, 1536, 512 + i * 96, rand, i === 1); cell(`duck${i}`, 1536, 512 + i * 96, 256, 96); }
    paintFootprint(ctx, 0, 768); cell("footprint", 0, 768, 32, 64);
    // rain streak and splash ring (white, tinted at runtime)
    const streak = ctx.createLinearGradient(0, 768, 0, 832);
    streak.addColorStop(0, "rgba(255,255,255,0)"); streak.addColorStop(0.7, "rgba(255,255,255,0.9)"); streak.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = streak; ctx.fillRect(40, 768, 3, 64); cell("rain", 39, 768, 5, 64);
    ctx.strokeStyle = "rgba(255,255,255,0.85)"; ctx.lineWidth = 2.5;
    ctx.beginPath(); ctx.arc(96, 800, 26, 0, 6.28); ctx.stroke(); cell("splash", 64, 768, 64, 64);

    const source = new CanvasSource({ resource: canvas, scaleMode: "linear", autoGenerateMipmaps: true });
    const texture = new Texture({ source });
    const cache = new Map<string, Texture>();
    return {
        texture,
        frame: (name: string): Texture => {
            let t = cache.get(name);
            if (!t) { t = new Texture({ source, frame: frames.get(name) }); cache.set(name, t); }
            return t;
        }
    };
}

export function warAtlas(): Atlas { return atlas ??= buildAtlas(); }

interface Placed { readonly x: number, readonly y: number, readonly r: number }

const CHUNK = 96; // game units per culling chunk

class WarDecalsClass {
    private _container?: Container;
    private _chunks: Container[][] = [];

    clear(): void {
        this._container?.destroy({ children: true });
        this._container = undefined;
        this._chunks = [];
    }

    /** how much of the marks snow covers (1.05: 0 none, 0.5 under snow) */
    cover(share: number): void {
        if (this._container) this._container.alpha = 1 - Math.min(1, Math.max(0, share));
    }

    build(seed: number, quality: string): void {
        this.clear();
        const terrain = MapManager.terrain;
        const width = MapManager.width, height = MapManager.height;
        if (!terrain || !width || !height) return;
        const { frame } = warAtlas();
        const rand = mulberry32(seed ^ 0xdec0);
        const density = quality === "high" ? 1 : quality === "medium" ? 0.75 : 0.55;

        // building footprints to keep clear (with a margin), from the map packet
        const blocked: Array<{ minX: number, minY: number, maxX: number, maxY: number }> = [];
        for (const mapObject of MapManager.objects) {
            const object = mapObject as unknown as { definition?: unknown, position: Vector, isBuilding?: boolean, orientation?: number, rotation?: number, scale?: number };
            if (!object.definition) continue;
            const definition = object.definition as { spawnHitbox?: { transform: (p: Vector, s: number, o: number) => { toRectangle: () => { min: Vector, max: Vector } } }, hitbox?: { transform: (p: Vector, s: number, o: number) => { toRectangle: () => { min: Vector, max: Vector } } } };
            const hitbox = definition.spawnHitbox ?? definition.hitbox;
            if (!hitbox) continue;
            try {
                const orientation = object.orientation ?? 0;
                const scale = typeof object.scale === "number" ? object.scale : 1;
                const rect = hitbox.transform(object.position, scale, orientation as never).toRectangle();
                const margin = object.isBuilding ? 2 : 0.5;
                blocked.push({ minX: rect.min.x - margin, minY: rect.min.y - margin, maxX: rect.max.x + margin, maxY: rect.max.y + margin });
            } catch { /* odd hitbox: ignore */ }
        }
        const isOpenMud = (x: number, y: number, r: number): boolean => {
            if (x < r || y < r || x > width - r || y > height - r) return false;
            for (const [dx, dy] of [[0, 0], [r, 0], [-r, 0], [0, r], [0, -r]]) {
                if (terrain.getFloor(Vec(x + dx, y + dy), 0) !== FloorNames.Grass) return false;
            }
            for (const b of blocked) {
                if (x + r > b.minX && x - r < b.maxX && y + r > b.minY && y - r < b.maxY) return false;
            }
            return true;
        };

        const root = this._container = new Container();
        root.zIndex = -4;
        const cols = Math.ceil(width / CHUNK), rows = Math.ceil(height / CHUNK);
        // draw order inside a chunk: low stains first, objects last
        const layers = ["stain", "scar", "hole", "board", "wire", "debris"] as const;
        const chunkLayers: Array<Array<Record<string, Container>>> = [];
        for (let cx = 0; cx < cols; cx++) {
            this._chunks[cx] = []; chunkLayers[cx] = [];
            for (let cy = 0; cy < rows; cy++) {
                const chunk = new Container();
                const sub: Record<string, Container> = {};
                for (const layer of layers) { sub[layer] = new Container(); chunk.addChild(sub[layer]); }
                chunk.visible = false;
                root.addChild(chunk);
                this._chunks[cx][cy] = chunk;
                chunkLayers[cx][cy] = sub;
            }
        }
        const placed: Placed[] = [];
        const clearOf = (x: number, y: number, r: number): boolean => !placed.some(p => (p.x - x) ** 2 + (p.y - y) ** 2 < ((p.r + r) * 0.75) ** 2);
        const add = (layer: typeof layers[number], name: string, x: number, y: number, size: number, rotation: number, aspect = 1, alpha = 1, tint = 0xffffff): Sprite => {
            const sprite = new Sprite(frame(name));
            sprite.anchor.set(0.5);
            sprite.position.set(x * PIXI_SCALE, y * PIXI_SCALE);
            const texture = sprite.texture;
            sprite.scale.set((size * PIXI_SCALE) / texture.width, (size * PIXI_SCALE * aspect) / texture.width);
            sprite.rotation = rotation;
            sprite.alpha = alpha;
            sprite.tint = tint;
            const cx = Math.min(cols - 1, Math.max(0, Math.floor(x / CHUNK))), cy = Math.min(rows - 1, Math.max(0, Math.floor(y / CHUNK)));
            chunkLayers[cx][cy][layer].addChild(sprite);
            return sprite;
        };
        const shade = (): number => { const v = Math.round(215 + rand() * 40); return (v << 16) | (v << 8) | v; };
        const area = (width * height) / 10000;
        // `grassKeep`: how much of this decal survives on grassland (the shelled mud regions keep it all)
        const scatter = (perBlock: number, radius: () => number, place: (x: number, y: number, r: number) => void, spacing = true, grassKeep = 0.3): void => {
            const count = Math.round(perBlock * area * density);
            for (let i = 0, attempts = 0; i < count && attempts < count * 6; attempts++) {
                const x = rand() * width, y = rand() * height, r = radius();
                // a spot the zone rules out still uses up its slot, so each area keeps its own density
                if (rand() > grassKeep + (1 - grassKeep) * mudZone(x, y)) { i++; continue; }
                if (!isOpenMud(x, y, r * 0.6) || (spacing && !clearOf(x, y, r))) continue;
                if (spacing) placed.push({ x, y, r });
                place(x, y, r);
                i++;
            }
        };

        scatter(2.2, () => 3 + rand() * 4, (x, y, r) => add("stain", `mud${Math.floor(rand() * 2)}`, x, y, r * 2.2, rand() * 6.28, 0.7 + rand() * 0.3, 0.8), false, 0.3);
        scatter(2.6, () => 1.6 + rand() * 2.4, (x, y, r) => add("stain", `puddle${Math.floor(rand() * 3)}`, x, y, r * 2.2, rand() * 6.28, 0.55 + rand() * 0.4, 0.95, shade()), true, 0.35);
        scatter(1.6, () => 3.5 + rand() * 3, (x, y, r) => add("scar", `scorch${Math.floor(rand() * 3)}`, x, y, r * 2.4, rand() * 6.28, 0.8 + rand() * 0.2, 0.85), true, 0.25);
        scatter(1.0, () => 5 + rand() * 3, (x, y, r) => add("scar", `ruts${Math.floor(rand() * 2)}`, x, y, r * 2.4, rand() * 6.28, 0.5, 0.75), true, 0.3);
        scatter(3.4, () => 5.5 + rand() * 4, (x, y, r) => add("hole", `crater${Math.floor(rand() * 3)}`, x, y, r * 2.35, rand() * 6.28, 0.9 + rand() * 0.1, 1, shade()), true, 0.15);
        scatter(4.2, () => 3 + rand() * 2.2, (x, y, r) => add("hole", `flooded${Math.floor(rand() * 3)}`, x, y, r * 2.4, rand() * 6.28, 0.9 + rand() * 0.1, 1, shade()), true, 0.12);
        scatter(9, () => 1 + rand() * 1.3, (x, y, r) => add("hole", `hole${Math.floor(rand() * 4)}`, x, y, r * 2.3, rand() * 6.28, 0.85 + rand() * 0.15, 1, shade()), true, 0.3);
        scatter(6.5, () => 0.35 + rand() * 0.35, (x, y, r) => add("debris", `debris${Math.floor(rand() * 8)}`, x, y, r * 3.2, rand() * 6.28), false, 0.45);

        // barbed-wire lines: two to four coils end to end
        scatter(0.3, () => 5, (x, y) => {
            const angle = rand() * 6.28, n = 2 + Math.floor(rand() * 3);
            for (let i = 0; i < n; i++) {
                const px = x + Math.cos(angle) * i * 7.2, py = y + Math.sin(angle) * i * 7.2;
                if (!isOpenMud(px, py, 3)) break;
                add("wire", `wire${Math.floor(rand() * 2)}`, px, py, 8, angle + (rand() - 0.5) * 0.08, 1, 0.95);
            }
        });
        // duckboard walkways: a gently wandering chain of boards
        scatter(0.22, () => 4, (x, y) => {
            let angle = rand() * 6.28, px = x, py = y;
            const n = 4 + Math.floor(rand() * 7);
            for (let i = 0; i < n; i++) {
                if (!isOpenMud(px, py, 1.4)) break;
                add("board", `duck${rand() < 0.22 ? 1 : 0}`, px, py, 4.4, angle, 0.375, 1, shade());
                placed.push({ x: px, y: py, r: 1.6 });
                angle += (rand() - 0.5) * 0.35;
                px += Math.cos(angle) * 4.25; py += Math.sin(angle) * 4.25;
            }
        });

        CameraManager.addObject(root);
    }

    /** show only the chunks around the camera */
    update(originX: number, originY: number, viewW: number, viewH: number): void {
        if (!this._container) return;
        const margin = 14;
        const x0 = Math.floor((originX - margin) / CHUNK), x1 = Math.floor((originX + viewW + margin) / CHUNK);
        const y0 = Math.floor((originY - margin) / CHUNK), y1 = Math.floor((originY + viewH + margin) / CHUNK);
        for (let cx = 0; cx < this._chunks.length; cx++) {
            const column = this._chunks[cx];
            for (let cy = 0; cy < column.length; cy++) column[cy].visible = cx >= x0 && cx <= x1 && cy >= y0 && cy <= y1;
        }
    }
}

export const WarDecals = new WarDecalsClass();
